// Phase 9A.9.1 — useSignalCalibrationSummary hook.
//
// Read-only consumer of `public.get_signal_calibration_summary` RPC.
// The RPC is the ONLY authenticated path to aggregate signal-
// calibration insight; the hook never queries `signal_card_grades`,
// `daily_intelligence_ledger`, `fixture_market_closing_lines`,
// `fixture_market_odds_history`, `provider_ingestion_runs`, or
// `fair_price_predictions` directly.
//
// Trust contract:
//   - No external HTTP. No provider API. No LLM. No PDF.
//   - Soft-fails on RPC error: surfaces `"Signal health unavailable."`
//     and returns null summary; never throws.
//   - Defensive projection — unknown / wrong-shape RPC payload
//     resolves to null instead of leaking through to the UI.
//   - Auth-gated: when no user is signed in, the hook returns null
//     summary + no error (the panel renders the resting state).

import { useMemo } from "react";

import { useAuthSession } from "../auth/useAuthSession.ts";
import { useRpcQuery } from "../data/useRpcQuery.ts";
import {
  projectSignalCalibrationSummary,
  type SignalCalibrationSummary,
} from "./signal-calibration-format.ts";

export interface UseSignalCalibrationSummaryState {
  summary: SignalCalibrationSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const UNAVAILABLE_COPY = "Signal health unavailable.";

export function useSignalCalibrationSummary(): UseSignalCalibrationSummaryState {
  const { user } = useAuthSession();
  const q = useRpcQuery<unknown>("get_signal_calibration_summary", {
    enabled: Boolean(user),
  });

  const summary = useMemo<SignalCalibrationSummary | null>(
    () => (q.error ? null : projectSignalCalibrationSummary(q.data)),
    [q.data, q.error],
  );

  const error = useMemo<string | null>(() => {
    if (!user) return null;
    if (q.error) return UNAVAILABLE_COPY;
    // A wrong-shape payload that projects to null is also "unavailable",
    // but only once the cold load has resolved with a value.
    if (!q.isLoading && q.data != null && summary === null) return UNAVAILABLE_COPY;
    return null;
  }, [user, q.error, q.isLoading, q.data, summary]);

  return useMemo<UseSignalCalibrationSummaryState>(
    () => ({
      summary: user ? summary : null,
      loading: user ? q.isLoading : false,
      error,
      refetch: async () => {
        await q.refetch();
      },
    }),
    [user, summary, q.isLoading, error, q.refetch],
  );
}
